const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PHASE_PROVENANCE_KEYS = new Set([
  'trigger',
  'kind',
  'sourceConsultationId',
  'sourcePreviousConsultationId',
  'sourceFollowupEditRevision',
  'sourcePublication',
  'createdAt',
  'workflowAction',
  'appointmentSha256',
  'appointmentClientId',
  'consultationOrdinal',
]);
const REVIEWED_PROVENANCE_KEYS = new Set([
  'trigger', 'kind', 'sourceConsultationId', 'sourcePreviousConsultationId',
  'sourceFollowupEditRevision', 'sourcePublication', 'createdAt',
  'workflowAction', 'updateScope', 'appointmentSha256', 'safetySnapshotSha256',
  'appointmentClientId', 'consultationOrdinal',
]);
const MANUAL_UPDATE_PROVENANCE_KEYS = new Set([
  'trigger', 'kind', 'sourceConsultationId', 'sourcePreviousConsultationId',
  'sourceFollowupEditRevision', 'sourcePublication', 'createdAt',
  'workflowAction', 'updateScope', 'sourcePlanSha256', 'safetySnapshotSha256',
]);

const PROOF_FIELDS = [
  'id',
  'owner_id',
  'client_id',
  'consultant_name',
  'date',
  'observations',
  'blood_test_done',
  'dna_test_done',
  'nutritional_observations',
  'nutrition_plan',
  'mgd_plan_adoption_id',
  'supplements',
  'recipes',
  'fiche_frigo_json',
  'lab_results',
  'genetic_results',
  'ai_analysis',
  'notes_for_coach',
  'private_notes',
  'is_followup',
  'followup_data',
  'previous_consultation_id',
  'mgd_recommendation',
  'mgd_recommended_tests_text',
  'status',
  'is_deleted',
  'deleted_at',
  'deleted_by',
  'created_at',
  'intro_letter',
  'meal_recipes',
  'editorial_overrides',
  'protocol_phases',
  'active_phase_id',
  'client_app_publication',
  'followup_edit_revision',
  'label',
];

const saveLocks = new Map();

export function normalizeNutritionConsultationCloudRow(row, fallbackClientId = null) {
  if (!row || typeof row !== 'object') return null;
  const followupData = row.followupData ?? row.followup_data ?? null;
  const parsedFollowupWeek = Number(
    row.followupWeek ?? followupData?._followupWeek,
  );
  const immediateManualUpdate = followupData?._versionProvenance?.trigger
    === 'manual_program_update';
  return {
    ...row,
    clientId: row.clientId ?? row.client_id ?? fallbackClientId,
    consultantName: row.consultantName ?? row.consultant_name ?? 'Anissa',
    label: row.label ?? '',
    date: row.date ?? row.createdAt ?? row.created_at,
    observations: row.observations ?? '',
    bloodTestDone: row.bloodTestDone ?? row.blood_test_done ?? false,
    dnaTestDone: row.dnaTestDone ?? row.dna_test_done ?? false,
    nutritionalObservations: row.nutritionalObservations ?? row.nutritional_observations ?? '',
    nutritionPlan: row.nutritionPlan ?? row.nutrition_plan ?? row.plan_text ?? '',
    mgdPlanAdoptionId: row.mgdPlanAdoptionId ?? row.mgd_plan_adoption_id ?? null,
    supplements: row.supplements ?? '',
    recipes: row.recipes ?? '',
    ficheFrigoJson: row.ficheFrigoJson ?? row.fiche_frigo_json ?? null,
    labResults: row.labResults ?? row.lab_results ?? {},
    geneticResults: row.geneticResults ?? row.genetic_results ?? {},
    aiAnalysis: row.aiAnalysis ?? row.ai_analysis ?? null,
    aiDirectives: row.aiDirectives ?? row.ai_directives ?? '',
    notesForCoach: row.notesForCoach ?? row.notes_for_coach ?? '',
    privateNotes: row.privateNotes ?? row.private_notes ?? '',
    isFollowup: row.isFollowup ?? row.is_followup ?? false,
    followupData,
    followupWeek: immediateManualUpdate && parsedFollowupWeek === 0
      ? 0
      : (Number.isInteger(parsedFollowupWeek)
        && parsedFollowupWeek >= 1
        && parsedFollowupWeek <= 6
        ? parsedFollowupWeek
        : null),
    previousConsultationId: row.previousConsultationId
      ?? row.previous_consultation_id
      ?? null,
    mgdRecommendation: row.mgdRecommendation ?? row.mgd_recommendation ?? 'none',
    mgdRecommendedTestsText: row.mgdRecommendedTestsText
      ?? row.mgd_recommended_tests_text
      ?? '',
    status: row.status ?? 'questionnaire_recu',
    isDeleted: row.isDeleted ?? row.is_deleted ?? false,
    deletedAt: row.deletedAt ?? row.deleted_at ?? null,
    deletedBy: row.deletedBy ?? row.deleted_by ?? null,
    createdAt: row.createdAt ?? row.created_at,
    clientAppPublication: row.clientAppPublication ?? row.client_app_publication ?? null,
    followupEditRevision: Number.isInteger(row.followupEditRevision)
      ? row.followupEditRevision
      : (Number.isInteger(row.followup_edit_revision) ? row.followup_edit_revision : 0),
  };
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        if (value[key] === undefined) return result;
        result[key] = stableValue(value[key]);
        return result;
      }, {});
  }
  return value ?? null;
}

function normalizeInstant(value) {
  if (typeof value !== 'string' || !value.trim()) return value ?? null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : value;
}

export function buildNutritionConsultationProof(row) {
  return PROOF_FIELDS.reduce((proof, field) => {
    const value = row?.[field];
    proof[field] = field === 'date' || field.endsWith('_at')
      ? normalizeInstant(value)
      : stableValue(value);
    return proof;
  }, {});
}

export function stableNutritionConsultationProof(row) {
  return JSON.stringify(buildNutritionConsultationProof(row));
}

async function sha256PlanText(text, cryptoApi) {
  const subtle = cryptoApi?.subtle;
  if (!subtle || typeof TextEncoder === 'undefined') {
    throw new Error('Preuve cryptographique indisponible : sauvegarde non confirmée.');
  }
  const bytes = new TextEncoder().encode(String(text ?? '').replace(/\r\n/g, '\n'));
  const digest = await subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * V97.240 : pour une version manuelle d'un dossier externe, le serveur émet
 * lui-même la nouvelle autorité et réécrit `request_id` + `current_text_sha256`
 * dans `external_plan_input`. On adopte ces deux champs — et eux seuls — après
 * avoir vérifié que l'empreinte serveur correspond bien au plan envoyé.
 * Sans ça, la relecture divergeait toujours : la sauvegarde réussissait en base
 * mais l'éditeur affichait un conflit et repartait d'une révision périmée.
 */
async function adoptServerExternalAuthority(sentRow, canonical, cryptoApi) {
  const sent = sentRow?.editorial_overrides?.external_plan_input;
  const server = canonical?.editorial_overrides?.external_plan_input;
  if (!sent || typeof sent !== 'object' || !server || typeof server !== 'object') {
    return sentRow;
  }
  if (!UUID_RE.test(String(server.request_id || ''))
      || server.current_text_sha256 !== await sha256PlanText(sentRow.nutrition_plan, cryptoApi)) {
    throw new Error('Conflit de version : l’autorité externe renvoyée ne correspond pas au plan sauvegardé.');
  }
  return {
    ...sentRow,
    editorial_overrides: {
      ...sentRow.editorial_overrides,
      external_plan_input: {
        ...sent,
        request_id: server.request_id,
        current_text_sha256: server.current_text_sha256,
      },
    },
  };
}

export async function sha256NutritionConsultation(row, cryptoApi = globalThis.crypto) {
  const subtle = cryptoApi?.subtle;
  if (!subtle || typeof TextEncoder === 'undefined') {
    throw new Error('Preuve cryptographique indisponible : sauvegarde non confirmée.');
  }
  const bytes = new TextEncoder().encode(stableNutritionConsultationProof(row));
  const digest = await subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function assertPersistableRow(row) {
  if (!UUID_RE.test(String(row?.id || '')) || !UUID_RE.test(String(row?.client_id || ''))) {
    throw new Error('Identité de version nutrition invalide : sauvegarde refusée.');
  }
  if (!UUID_RE.test(String(row?.owner_id || ''))) {
    throw new Error('Propriétaire cloud introuvable : sauvegarde refusée.');
  }
  if (row?.is_deleted === true) {
    throw new Error('Une consultation supprimée ne peut pas être confirmée.');
  }
  if (row?.is_followup === true) {
    const provenance = row?.followup_data?._versionProvenance;
    // Les anciennes consultations de suivi restent lisibles/sauvegardables.
    // Dès qu'une version déclare le nouveau contrat de provenance, celui-ci
    // devient en revanche obligatoire et strictement chaîné.
    if (provenance && (provenance.kind !== 'followup_manual_version'
        || provenance.sourceConsultationId !== row.previous_consultation_id
        || !Number.isInteger(provenance.sourceFollowupEditRevision)
        || provenance.sourceFollowupEditRevision < 0)) {
      throw new Error('Provenance de la version de suivi incomplète : sauvegarde refusée.');
    }
    if (provenance && row.id === row.previous_consultation_id) {
      throw new Error('La version de suivi doit avoir un nouvel identifiant.');
    }
    if (provenance && (!Number.isInteger(row.followup_edit_revision)
        || row.followup_edit_revision < 0)) {
      throw new Error('Révision du brouillon de suivi invalide : sauvegarde refusée.');
    }
    if (provenance?.trigger === 'reviewed_appointment_adaptation') {
      const provenanceKeys = Object.keys(provenance);
      if (
        provenanceKeys.length !== REVIEWED_PROVENANCE_KEYS.size
        || provenanceKeys.some((key) => !REVIEWED_PROVENANCE_KEYS.has(key))
        || !['adapt', 'newVersion'].includes(provenance.workflowAction)
        || !['targeted', 'menus_recipes', 'full'].includes(provenance.updateScope)
        || (provenance.workflowAction === 'adapt'
          && !['targeted', 'menus_recipes'].includes(provenance.updateScope))
        || (provenance.workflowAction === 'newVersion' && provenance.updateScope !== 'full')
        || !/^[a-f0-9]{64}$/.test(String(provenance.appointmentSha256 || ''))
        || !/^[a-f0-9]{64}$/.test(String(provenance.safetySnapshotSha256 || ''))
        || provenance.appointmentClientId !== row.client_id
        || !Number.isInteger(provenance.consultationOrdinal)
        || provenance.consultationOrdinal !== row.followup_data?._followupWeek
      ) {
        throw new Error('Preuves du rendez-vous relu incomplètes : sauvegarde refusée.');
      }
    } else if (provenance?.trigger === 'manual_program_update') {
      const provenanceKeys = Object.keys(provenance);
      if (
        provenanceKeys.length !== MANUAL_UPDATE_PROVENANCE_KEYS.size
        || provenanceKeys.some((key) => !MANUAL_UPDATE_PROVENANCE_KEYS.has(key))
        || provenance.workflowAction !== 'manual'
        || !['targeted', 'menus_recipes', 'full'].includes(provenance.updateScope)
        || !/^[a-f0-9]{64}$/.test(String(provenance.sourcePlanSha256 || ''))
        || !/^[a-f0-9]{64}$/.test(String(provenance.safetySnapshotSha256 || ''))
        || row.followup_data?._followupWeek !== 0
      ) {
        throw new Error('Preuves de mise à jour manuelle incomplètes : sauvegarde refusée.');
      }
    } else if (provenance?.trigger === 'phase_transition') {
      const phaseDraft = row?.followup_data?.phaseTransitionDraft;
      const phaseRows = row?.protocol_phases?.phases;
      const activePhaseId = String(row?.active_phase_id || '').trim();
      const provenanceKeys = Object.keys(provenance);
      if (
        !phaseDraft
        || typeof phaseDraft !== 'object'
        || Array.isArray(phaseDraft)
        || Object.keys(phaseDraft).length !== 3
        || phaseDraft.sourceConsultationId !== row.previous_consultation_id
        || typeof phaseDraft.fromPhaseId !== 'string'
        || !phaseDraft.fromPhaseId.trim()
        || typeof phaseDraft.toPhaseId !== 'string'
        || !phaseDraft.toPhaseId.trim()
        || provenanceKeys.length !== PHASE_PROVENANCE_KEYS.size
        || provenanceKeys.some((key) => !PHASE_PROVENANCE_KEYS.has(key))
        || provenance.workflowAction !== 'transition'
        || !/^[a-f0-9]{64}$/.test(String(provenance.appointmentSha256 || ''))
        || provenance.appointmentClientId !== row.client_id
        || !Number.isInteger(provenance.consultationOrdinal)
        || provenance.consultationOrdinal !== row.followup_data?._followupWeek
        || !Array.isArray(phaseRows)
        || phaseRows.length < 2
        || activePhaseId !== phaseDraft.fromPhaseId.trim()
      ) {
        throw new Error('Preuves de transition de phase incomplètes : sauvegarde refusée.');
      }
      const fromIndex = phaseRows.findIndex((phase) => (
        phase && typeof phase === 'object'
        && !Array.isArray(phase)
        && String(phase.id || '') === phaseDraft.fromPhaseId.trim()
        && phase.status === 'active'
      ));
      const activeRows = phaseRows.filter((phase) => phase?.status === 'active');
      const fromRows = phaseRows.filter((phase) => String(phase?.id || '') === phaseDraft.fromPhaseId.trim());
      const toRows = phaseRows.filter((phase) => String(phase?.id || '') === phaseDraft.toPhaseId.trim());
      const nextPhase = fromIndex >= 0 ? phaseRows[fromIndex + 1] : null;
      if (
        phaseRows.some((phase) => !phase || typeof phase !== 'object' || Array.isArray(phase))
        || activeRows.length !== 1
        || fromRows.length !== 1
        || toRows.length !== 1
        || !nextPhase
        || String(nextPhase.id || '') !== phaseDraft.toPhaseId.trim()
        || nextPhase.status !== 'upcoming'
      ) {
        throw new Error('La phase cible ne correspond pas à la prochaine phase réelle : sauvegarde refusée.');
      }
    }
  }
}

async function runWithSaveLock(key, task) {
  const previous = saveLocks.get(key) || Promise.resolve();
  const current = previous.catch(() => {}).then(task);
  saveLocks.set(key, current);
  try {
    return await current;
  } finally {
    if (saveLocks.get(key) === current) saveLocks.delete(key);
  }
}

/**
 * Persiste une version puis la relit séparément. Le succès signifie que la
 * ligne canonique relue possède exactement le même contenu, propriétaire et
 * chaînage de provenance. Aucun fallback local n'est effectué ici.
 */
export async function persistNutritionConsultationCloudConfirmed({
  db,
  row,
  cryptoApi = globalThis.crypto,
  externalExpectedRequestId = null,
}) {
  assertPersistableRow(row);
  if (!db?.from) throw new Error('Cloud indisponible : sauvegarde non confirmée.');

  return runWithSaveLock(row.id, async () => {
    const revisioned = row?.followup_data?._versionProvenance?.kind === 'followup_manual_version';
    let expectedRow = row;
    let data = null;

    if (revisioned) {
      const expectedRevision = row.followup_edit_revision;
      const provenance = row?.followup_data?._versionProvenance || {};
      const reviewedAppointmentVersion = provenance.trigger === 'reviewed_appointment_adaptation';
      const manualProgramUpdateVersion = provenance.trigger === 'manual_program_update';
      const phaseTransitionVersion = provenance.trigger === 'phase_transition';
      const rpcName = reviewedAppointmentVersion
        ? 'sauvegarder_version_suivi_revue_compare_and_set'
        : manualProgramUpdateVersion
          ? 'sauvegarder_version_programme_manuel_compare_and_set'
        : phaseTransitionVersion
          ? 'sauvegarder_version_suivi_phase_compare_and_set'
          : null;
      if (!rpcName) {
        throw new Error('Cette provenance de suivi historique est en lecture seule — prépare un rendez-vous relu.');
      }
      const rpcPayload = reviewedAppointmentVersion
        ? {
          p_payload: row,
          p_expected_revision: expectedRevision,
          p_expected_appointment_sha256: provenance.appointmentSha256,
          p_expected_safety_snapshot_sha256: provenance.safetySnapshotSha256,
          p_expected_client_id: row.client_id,
          p_expected_source_consultation_id: row.previous_consultation_id,
          p_expected_consultation_ordinal: provenance.consultationOrdinal,
        }
        : manualProgramUpdateVersion
          ? {
            p_payload: row,
            p_expected_revision: expectedRevision,
            p_expected_source_plan_sha256: provenance.sourcePlanSha256,
            p_expected_safety_snapshot_sha256: provenance.safetySnapshotSha256,
            p_expected_client_id: row.client_id,
            p_expected_source_consultation_id: row.previous_consultation_id,
          }
        : phaseTransitionVersion
          ? {
            p_payload: row,
            p_expected_revision: expectedRevision,
            p_expected_client_id: row.client_id,
            p_expected_source_consultation_id: row.previous_consultation_id,
            p_expected_consultation_ordinal: row.followup_data?._followupWeek,
          }
        : null;
      const { data: canonical, error: casError } = await db.rpc(
        rpcName,
        rpcPayload,
      );
      if (casError || !canonical) {
        const error = new Error(casError?.message || 'Conflit de brouillon : une autre fenêtre a déjà enregistré ses changements.');
        if (casError) Object.assign(error, {
          code: casError.code,
          details: casError.details,
          hint: casError.hint,
          status: casError.status,
        });
        throw error;
      }
      const canonicalRevision = canonical.followup_edit_revision;
      const revisionIsValid = Number.isInteger(canonicalRevision)
        && (canonicalRevision === expectedRevision
          || canonicalRevision === expectedRevision + 1)
        && (expectedRevision === 0 || canonicalRevision === expectedRevision + 1);
      if (!revisionIsValid) {
        throw new Error('Conflit de brouillon : la révision cloud confirmée est invalide.');
      }
      expectedRow = { ...row, followup_edit_revision: canonicalRevision };
      if (manualProgramUpdateVersion) {
        expectedRow = await adoptServerExternalAuthority(expectedRow, canonical, cryptoApi);
      }
      data = canonical;
    } else {
      const external = row?.editorial_overrides?.external_plan_input;
      const usesExternalAuthority = external?.plan_input_mode
        === 'external_anonymized_dossier_v1';
      if (usesExternalAuthority
          && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
            .test(external?.synthesis_id || '')) {
        throw new Error('Preuve serveur de la synthèse requise pour sauvegarder ce plan.');
      }
      const usesExternalCompareAndSet = usesExternalAuthority
        && externalExpectedRequestId !== null;
      if (usesExternalCompareAndSet
          && (!UUID_RE.test(externalExpectedRequestId)
            || externalExpectedRequestId === external?.request_id)) {
        throw new Error('Version externe attendue invalide pour la sauvegarde.');
      }
      const rpcName = usesExternalCompareAndSet
        ? 'sauvegarder_plan_externe_cas_atomiquement'
        : usesExternalAuthority
          ? 'sauvegarder_plan_externe_depuis_synthese_atomiquement'
        : 'sauvegarder_consultation_nutritionnelle_initiale';
      const rpcPayload = usesExternalCompareAndSet
        ? {
          p_payload: row,
          p_synthesis_id: external.synthesis_id,
          p_new_request_id: external.request_id,
          p_expected_request_id: externalExpectedRequestId,
        }
        : usesExternalAuthority
        ? {
          p_payload: row,
          p_synthesis_id: external.synthesis_id,
          p_request_id: external.request_id,
        }
        : { p_payload: row };
      const { data: persisted, error: upsertError } = await db.rpc(rpcName, rpcPayload);
      const canonical = usesExternalAuthority ? persisted?.consultation : persisted;
      if (upsertError || !canonical) {
        const error = new Error(upsertError?.message || 'Sauvegarde cloud refusée.');
        if (upsertError) Object.assign(error, {
          code: upsertError.code,
          details: upsertError.details,
          hint: upsertError.hint,
          status: upsertError.status,
        });
        throw error;
      }
      data = canonical;
    }

    if (!data) {
      const { data: reread, error: readError } = await db
        .from('nutrition_consultations')
        .select('*')
        .eq('id', row.id)
        .eq('client_id', row.client_id)
        .eq('owner_id', row.owner_id)
        .maybeSingle();
      if (readError || !reread) {
        throw new Error(readError?.message || 'La version sauvegardée est introuvable dans le cloud.');
      }
      data = reread;
    }

    if (!revisioned) {
      if (!Number.isInteger(data.followup_edit_revision)
          || data.followup_edit_revision < 0) {
        throw new Error('La rÃ©vision serveur de la consultation est invalide.');
      }
      // Le trigger V97.202 incrÃ©mente autoritativement les lignes historiques
      // aussi. Le reste du payload doit rester identique au brouillon envoyÃ©.
      expectedRow = {
        ...row,
        followup_edit_revision: data.followup_edit_revision,
      };
    }

    const expectedHash = await sha256NutritionConsultation(expectedRow, cryptoApi);
    const actualHash = await sha256NutritionConsultation(data, cryptoApi);
    if (actualHash !== expectedHash) {
      throw new Error('Conflit de version : la ligne cloud relue ne correspond pas au brouillon sauvegardé.');
    }

    const expectedProvenance = stableValue(expectedRow?.followup_data?._versionProvenance);
    const actualProvenance = stableValue(data?.followup_data?._versionProvenance);
    if (JSON.stringify(actualProvenance) !== JSON.stringify(expectedProvenance)) {
      throw new Error('La provenance cloud de la version ne correspond pas au brouillon sauvegardé.');
    }

    return {
      row: data,
      contentSha256: actualHash,
      provenance: actualProvenance,
    };
  });
}

function cloneJson(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

export function buildFollowupNutritionVersion(sourceConsultation, draft, context = {}) {
  const sourceId = String(context.sourceConsultationId || sourceConsultation?.id || '').trim();
  const clientId = String(context.clientId || draft?.clientId || sourceConsultation?.clientId || '').trim();
  const id = String(context.idFactory?.() || globalThis.crypto?.randomUUID?.() || '').trim();
  const createdAt = String(context.now?.() || new Date().toISOString());
  const followupWeek = Number(context.followupWeek ?? draft?.followupWeek ?? sourceConsultation?.followupWeek);
  const trigger = context?.provenance?.trigger;

  if (!UUID_RE.test(sourceId) || !UUID_RE.test(clientId) || !UUID_RE.test(id) || id === sourceId) {
    throw new Error('Impossible de créer une version de suivi immuable.');
  }
  if (!['reviewed_appointment_adaptation', 'manual_program_update', 'phase_transition'].includes(trigger)) {
    throw new Error('Une nouvelle version exige un rendez-vous relu, une mise à jour manuelle autorisée ou une transition de phase.');
  }
  const validFollowupWeek = trigger === 'manual_program_update'
    ? followupWeek === 0
    : Number.isInteger(followupWeek) && followupWeek >= 1 && followupWeek <= 6;
  if (!validFollowupWeek) {
    throw new Error('Semaine de suivi manquante : choisis la période avant de sauvegarder.');
  }

  const sourcePublication = cloneJson(
    sourceConsultation?.client_app_publication ?? sourceConsultation?.clientAppPublication ?? null,
  );
  const callerProvenance = cloneJson(context.provenance || {});
  const sourcePreviousConsultationId = sourceConsultation?.previousConsultationId
    || sourceConsultation?.previous_consultation_id
    || null;
  const sourceFollowupEditRevision = Number.isInteger(sourceConsultation?.followupEditRevision)
    ? sourceConsultation.followupEditRevision
    : (Number.isInteger(sourceConsultation?.followup_edit_revision)
      ? sourceConsultation.followup_edit_revision
      : 0);
  const versionProvenance = {
    ...callerProvenance,
    kind: 'followup_manual_version',
    sourceConsultationId: sourceId,
    sourcePreviousConsultationId,
    sourceFollowupEditRevision,
    sourcePublication,
    createdAt,
  };

  return {
    ...cloneJson(sourceConsultation || {}),
    ...cloneJson(draft || {}),
    id,
    clientId,
    date: createdAt,
    createdAt,
    isFollowup: true,
    followupWeek,
    previousConsultationId: sourceId,
    followupData: {
      ...cloneJson(sourceConsultation?.followupData || sourceConsultation?.followup_data || {}),
      ...cloneJson(draft?.followupData || {}),
      _followupWeek: followupWeek,
      _versionProvenance: versionProvenance,
    },
    status: 'a_valider',
    followupEditRevision: 0,
    isDeleted: false,
    deletedAt: null,
    deletedBy: null,
    client_app_publication: null,
    clientAppPublication: null,
  };
}

export async function awaitConfirmedNutritionReload(saved, onPlanSaved) {
  if (typeof onPlanSaved === 'function') await onPlanSaved(saved);
  return saved;
}
